/** @module PackItem */

import { zip } from 'ramda';

import { isIngredientEqual } from '@/models/core/Ingredient';

import {
  PRODUCT_NAME_TO_CATEGORIES_LIST_MAP,
  PRODUCT_NAMES,
  PRODUCT_REFERENCE_TO_SIZE_MAP,
  PRODUCT_REFERENCES,
  FORMULA_USAGE,
  EYE_SERUM_GENERIC_FORMULA
} from '@/constants/treatment';

/**
 * Product availability status.
 *
 * @typedef {'available' | 'not-available' | 'out-of-stock'} ProductStatus
 */

/**
 * May represent a bottle of serum or complementary product.
 * It's a single bottle which could be sold only as a part of ProductPack,
 * not as a separate item.
 *
 * @typedef {Object} PackItem
 * @property {string} name
 * @property {string} reference
 * @property {Size} size
 * @property {string[]} categories
 * @property {string} usage
 * @property {Price} price
 * @property {ProductStatus} status
 * @property {number} stockQuantity
 * @property {Ingredient[]|null} ingredients
 * @property {string|null} formulaId
 */

const PRODUCT_REFERENCE_TO_NAME_MAP = {
  faceSerum: 'faceSerum',
  eyeContourSerum: 'eyeContourSerum',
  [PRODUCT_REFERENCES.NEXULTRA_O1]: 'nexUltraO',
  [PRODUCT_REFERENCES.NEXULTRA_O2]: 'nexUltraO',
  [PRODUCT_REFERENCES.NEXULTRA_1]: 'nexUltra1',
  [PRODUCT_REFERENCES.NEXULTRA_2]: 'nexUltra2',
  [PRODUCT_REFERENCES.NEXULTRA_3]: 'nexUltra3',
  [PRODUCT_REFERENCES.NEXULTRA_B1]: 'nexUltraB',
  [PRODUCT_REFERENCES.NEXULTRA_B2]: 'nexUltraB',
  [PRODUCT_REFERENCES.UNIVERSKIN_H]: 'universkinH',
  [PRODUCT_REFERENCES.UNIVERSKIN_M]: 'universkinM',
  [PRODUCT_REFERENCES.NEXULTRA_L1]: 'nexUltraL',
  [PRODUCT_REFERENCES.GEL_30]: 'gel',
  [PRODUCT_REFERENCES.GEL_100]: 'gel',
  [PRODUCT_REFERENCES.SUNSCREEN_SPF]: 'sunscreenSPF',
  [PRODUCT_REFERENCES.SUNSCREEN_FLD]: 'sunscreenFLD',
  [PRODUCT_REFERENCES.SUNSCREEN_CRM]: 'sunscreenCRM',
  [PRODUCT_REFERENCES.NEXULTRA_UV]: 'nexUltraUV',
  [PRODUCT_REFERENCES.NEXULTRA_P_LIGHT]: 'nexUltraPLight',
  [PRODUCT_REFERENCES.NEXULTRA_P_RICH]: 'nexUltraPRich',
  [PRODUCT_REFERENCES.FREE_SAMPLE]: 'freeSample'
};

const DEFAULT_USAGE = {
  [PRODUCT_NAMES.FACE_SERUM]: '',
  [PRODUCT_NAMES.EYE_CONTOUR_SERUM]: '',

  [PRODUCT_NAMES.NEXULTRA_O]: FORMULA_USAGE.MORNING_AND_EVENING,
  [PRODUCT_NAMES.NEXULTRA_P_LIGHT]: FORMULA_USAGE.EVENING_ONLY,
  [PRODUCT_NAMES.NEXULTRA_P_RICH]: FORMULA_USAGE.MORNING_AND_EVENING,
  [PRODUCT_NAMES.NEXULTRA_1]: FORMULA_USAGE.MORNING_ONLY,
  [PRODUCT_NAMES.NEXULTRA_2]: FORMULA_USAGE.MORNING_ONLY,
  [PRODUCT_NAMES.NEXULTRA_3]: FORMULA_USAGE.MORNING_AND_EVENING,
  [PRODUCT_NAMES.NEXULTRA_B]: FORMULA_USAGE.MORNING_ONLY,
  [PRODUCT_NAMES.NEXULTRA_L]: FORMULA_USAGE.MORNING_ONLY,
  [PRODUCT_NAMES.UNIVERSKIN_H]: FORMULA_USAGE.MORNING_AND_EVENING,
  [PRODUCT_NAMES.UNIVERSKIN_M]: FORMULA_USAGE.MORNING_AND_EVENING,
  [PRODUCT_NAMES.SUNSCREEN_SPF]: FORMULA_USAGE.MORNING_ONLY,
  [PRODUCT_NAMES.SUNSCREEN_CRM]: FORMULA_USAGE.MORNING_ONLY,
  [PRODUCT_NAMES.SUNSCREEN_FLD]: FORMULA_USAGE.MORNING_ONLY,
  [PRODUCT_NAMES.GEL]: FORMULA_USAGE.MORNING_AND_EVENING,
  [PRODUCT_NAMES.NEXULTRA_UV]: FORMULA_USAGE.MORNING_ONLY
};

/** PackItem constructor.
 *
 * @function
 * @param {string} reference - E.g. nexUltraB1
 * @param {Price} price
 * @param {ProductStatus} status
 * @param {number} stockQuantity
 * @param {string} [usage]
 * @param {Ingredient[]} [ingredients]
 * @param {string} [formulaId] - E.g. generic formula id
 * @param {Size} [size]
 * @return {PackItem}
 */
export const PackItem = ({
  reference,
  price,
  status,
  stockQuantity,
  usage,
  ingredients,
  formulaId,
  size
}) => {
  const name = PRODUCT_REFERENCE_TO_NAME_MAP[reference];
  const defaultUsage = DEFAULT_USAGE[name];

  return {
    name,
    reference,
    size: size || PRODUCT_REFERENCE_TO_SIZE_MAP[reference],
    categories: PRODUCT_NAME_TO_CATEGORIES_LIST_MAP[name],
    ingredients: ingredients || null,
    formulaId: formulaId || null,
    usage: usage || defaultUsage,
    status: status || 'available',
    stockQuantity: stockQuantity || 0,
    price
  };
};

/**
 * Set usage on packItem.
 *
 * @function
 * @param {string} usage
 * @param {PackItem} packItem
 * @return {PackItem}
 */
export const setPackItemUsage = (usage, packItem) => {
  return PackItem({ ...packItem, usage });
};

/**
 * Set price on packItem.
 *
 * @function
 * @param {Price} price
 * @param {PackItem} packItem
 * @return {PackItem}
 */
export const setPackItemPrice = (price, packItem) => {
  return PackItem({ ...packItem, price });
};

/**
 * Set status on packItem.
 *
 * @function
 * @param {string} status
 * @param {PackItem} packItem
 * @return {PackItem}
 */
export const setPackItemStatus = (status, packItem) => {
  return PackItem({ ...packItem, status });
};

/**
 * Set stock quantity on packItem.
 *
 * @function
 * @param {number} stockQuantity
 * @param {PackItem} packItem
 * @return {PackItem}
 */
export const setPackItemStockQuantity = (stockQuantity, packItem) => {
  return PackItem({ ...packItem, stockQuantity });
};

export const getPackItemStockQuantity = packItem => packItem.stockQuantity;

/**
 * Get unique reference for product.
 *
 * Notice, that reference may not be unique for all products.
 * E.g. eye serum generic formulas each has the same reference, but
 * they are considered as different products. So we have this
 * 'reference' field for historical reasons. For uniqueness checks
 * use getPackItemUniqueId function instead.
 *
 * @function
 * @param {PackItem} packItem
 * @return {string}
 */
export const getPackItemReference = packItem => packItem.reference;

/**
 * Get unique id for pack item.
 *
 * @function
 * @param {PackItem} packItem
 * @return {string}
 */
export const getPackItemUniqueId = packItem => packItem.formulaId || packItem.reference;

/**
 * Get optional formula id.
 *
 * @function
 * @param {PackItem} packItem
 * @return {string | null}
 */
export const getPackItemFormulaId = packItem => packItem.formulaId;

/**
 * Get categories.
 *
 * @function
 * @param {PackItem} packItem
 * @return {string[]}
 */
export const getPackItemCategories = packItem => packItem.categories;

/**
 * Get product size.
 *
 * @function
 * @param {PackItem} packItem
 * @return {Size}
 */
export const getPackItemSize = packItem => packItem.size;

/**
 * Set pack item size.
 *
 * @param {Size} size
 * @param {PackItem} packItem
 * @return {PackItem}
 */
export const setPackItemSize = (size, packItem) =>
  PackItem({
    ...packItem,
    size
  });

/**
 * Get product status.
 *
 * @function
 * @param {PackItem} packItem
 * @return {ProductStatus}
 */
export const getPackItemStatus = packItem => packItem.status;

/**
 * Get product price.
 *
 * @function
 * @param {PackItem} packItem
 * @return {Price}
 */
export const getPackItemPrice = packItem => packItem.price;

/**
 * Get product ingredients.
 *
 * @function
 * @param {PackItem} packItem
 * @return {Ingredient[]}
 */
export const getPackItemIngredients = packItem => packItem.ingredients;

/**
 * Get product usage.
 *
 * @function
 * @param {PackItem} packItem
 * @return {string}
 */
export const getPackItemUsage = packItem => packItem.usage;

/**
 * Get product name.
 *
 * @function
 * @param {PackItem} packItem
 * @return {string}
 */
export const getPackItemName = packItem => packItem.name;

/**
 * Check if pack item contains ingredients.
 *
 * @function
 * @param {PackItem} packItem
 * @return {boolean}
 */
export const hasPackItemIngredients = packItem =>
  packItem.ingredients && packItem.ingredients.length > 0;

/**
 * Check for equality two pack items.
 *
 * @function
 * @param {PackItem} packItemA
 * @param {PackItem} packItemB
 * @return {boolean}
 */
export const isPackItemEqual = (packItemA, packItemB) => {
  if (getPackItemName(packItemA) !== getPackItemName(packItemB)) {
    return false;
  }

  if (getPackItemUsage(packItemA) !== getPackItemUsage(packItemB)) {
    return false;
  }

  const formulaIdA = getPackItemFormulaId(packItemA);
  const formulaIdB = getPackItemFormulaId(packItemB);

  if (formulaIdA || formulaIdB) {
    return formulaIdA === formulaIdB;
  }

  if (hasPackItemIngredients(packItemA) && hasPackItemIngredients(packItemB)) {
    const ingredientsA = getPackItemIngredients(packItemA);
    const ingredientsB = getPackItemIngredients(packItemB);

    if (ingredientsA.length !== ingredientsB.length) {
      return false;
    }

    return zip(ingredientsA, ingredientsB).every(([first, second]) =>
      isIngredientEqual(first, second)
    );
  }

  return getPackItemReference(packItemA) === getPackItemReference(packItemB);
};

export const getPackItemNameByReference = reference => {
  return PRODUCT_REFERENCE_TO_NAME_MAP[reference];
};

export const getPackItemNameByUniqueId = id => {
  if (Object.values(EYE_SERUM_GENERIC_FORMULA).includes(id)) {
    return PRODUCT_NAMES.EYE_CONTOUR_SERUM;
  }

  return PRODUCT_REFERENCE_TO_NAME_MAP[id];
};
